import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from './ui/card';
import { Leaf, Cpu, Users, Trophy, Lightbulb, Sparkles, Target, Award } from 'lucide-react';

export const Categories = () => {
  const categories = [
    {
      title: 'Best Sustainable Stand',
      description: 'Recognizes excellence in sustainable design across the entire lifecycle',
      icon: Leaf,
      badge: 'Gold, Silver & Bronze Awards'
    },
    {
      title: 'Best Use of Technology',
      description: 'Honors the most intelligent and purposeful use of technology to enhance storytelling',
      icon: Cpu,
      badge: 'Gold, Silver & Bronze Awards'
    },
    {
      title: 'Best Visitor Experience',
      description: 'Celebrates stands that create memorable and engaging visitor journeys',
      icon: Users,
      badge: 'Gold, Silver & Bronze Awards'
    },
    {
      title: "Judge's Choice Award",
      description: 'Selected by the jury for exceptional creativity and innovation',
      icon: Trophy,
      badge: 'Gold, Silver & Bronze Awards'
    },
    {
      title: 'Best Brand Experience & Storytelling',
      description: 'Rewards the stand that transforms a brand\'s essence into an immersive, coherent story',
      icon: Sparkles,
      badge: 'Gold, Silver & Bronze Awards'
    },
    {
      title: 'Most Innovative Stand Concept',
      description: 'Celebrates breakthrough innovation that meaningfully redefines exhibition environments',
      icon: Lightbulb,
      badge: 'Gold, Silver & Bronze Awards'
    },
    {
      title: 'Best Structural Design & Engineering',
      description: 'Celebrates precision engineering and intelligent construction',
      icon: Target,
      badge: 'Gold, Silver & Bronze Awards'
    },
    {
      title: 'Best Creative Concept',
      description: 'Honors the stand concept that demonstrates outstanding originality and strategic imagination',
      icon: Award,
      badge: 'Gold, Silver & Bronze Awards'
    }
  ];

  return (
    <section
      id="categories"
      className="bg-[#0B1220] text-white py-16 sm:py-20"
      data-testid="categories-section"
    >
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <p className="text-sm font-semibold text-amber-400 tracking-wider uppercase mb-3">
            AWARD CATEGORIES
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            8 Categories. Gold, Silver & Bronze Awards.
          </h2>
          <p className="text-lg text-gray-300 max-w-3xl mx-auto">
            Each category awards top 3 winners with prestigious Gold, Silver, and Bronze recognitions
          </p>
        </div>

        {/* Categories Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="categories-grid">
          {categories.map((category, index) => (
            <Card
              key={index}
              className="group p-6 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 transition-all duration-200 cursor-pointer"
              data-testid={`category-card-${index}`}
            >
              <div className="flex flex-col gap-3">
                <div className="flex-shrink-0 h-12 w-12 rounded-lg bg-amber-500/20 flex items-center justify-center group-hover:bg-amber-500/30 transition-colors">
                  <category.icon className="h-6 w-6 text-amber-400" aria-hidden="true" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-white mb-2 group-hover:text-amber-400 transition-colors">
                    {category.title}
                  </h3>
                  <p className="text-sm text-gray-300 leading-relaxed mb-3">
                    {category.description}
                  </p>
                  <div className="flex items-center gap-1.5 text-xs text-amber-400 font-semibold">
                    <Award className="h-3.5 w-3.5" />
                    {category.badge}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* View All Criteria Button */}
        <div className="mt-12 text-center">
          <Link
            to="/categories"
            className="inline-flex items-center gap-2 bg-white hover:bg-gray-100 text-gray-900 font-semibold px-8 py-3 rounded-full transition-colors duration-200"
            data-testid="view-all-criteria-button"
          >
            VIEW ALL CRITERIA
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
      </div>
    </section>
  );
};